import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import myconfig from '../Config';

const COLUMNS: { key: keyof Api.ExamRegistration | 'DepartmentName'; label: string }[] = [
  { key: 'Id',             label: 'Id' },
  { key: 'Firstname',      label: 'Fornavn' },
  { key: 'Lastname',       label: 'Etternavn' },
  { key: 'FirstnameTamil', label: 'Fornavn (tamilsk)' },
  { key: 'LastnameTamil',  label: 'Etternavn (tamilsk)' },
  { key: 'DateOfBirth',    label: 'Fødselsdato' },
  { key: 'DepartmentName', label: 'Avdeling' },
  { key: 'ClassName',      label: 'Klasse' },
  { key: 'ParentEmail',    label: 'Forelder e-post' },
  { key: 'ParentMobile',   label: 'Forelder mobil' },
  { key: 'Parent2Email',   label: 'Forelder 2 e-post' },
  { key: 'Parent2Mobile',  label: 'Forelder 2 mobil' },
  { key: 'DateCreated',    label: 'Registrert' },
];

const formatDob = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${mi}`;
};

const tableStyle: React.CSSProperties = { borderCollapse: 'collapse', fontSize: '0.875rem', width: '100%' };
const thStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px', background: '#f0f0f0', textAlign: 'left', position: 'sticky', top: 0 };
const tdStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px' };

const ExamRegistrationReportView: React.FC<RouteComponentProps> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<Api.ExamRegistration[]>([]);
  const [departments, setDepartments] = useState<Api.Department[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const [data, depts] = await Promise.all([
          RestService.get<Api.ExamRegistration[]>('/api/examregistration/All'),
          RestService.get<Api.Department[]>('/api/examregistration/GetDepartments'),
        ]);
        setRows(data ?? []);
        setDepartments(depts ?? []);
      } catch (e) {
        toast.error(`Kunne ikke laste rapport: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const departmentName = (id?: number) => departments.find(d => d.Id === id)?.Name ?? '';

  const cellValue = (row: Api.ExamRegistration, key: typeof COLUMNS[number]['key']): string => {
    if (key === 'DepartmentName') return departmentName(row.DepartmentId);
    if (key === 'DateOfBirth') return formatDob(row.DateOfBirth);
    if (key === 'DateCreated') return formatDateTime(row.DateCreated);
    const v = (row as any)[key];
    return v == null ? '' : String(v);
  };

  const exportXlsx = async () => {
    try {
      const token = localStorage.getItem('myToken');
      const response = await fetch(`${myconfig.rootUrl}/api/examregistration/ExportXlsx`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eksamensregistrering-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Kunne ikke eksportere: ${e}`);
    }
  };

  return (
    <>
      <OKomBreadcrumb path='Rapporter, Eksamensregistrering'></OKomBreadcrumb>
      {isLoading && <OKomLoading text='Henter rapport...' />}
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <div className="ods-grid__column--12">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
            <h2 style={{ margin: 0 }}>Eksamensregistrering — rapport ({rows.length})</h2>
            <button
              type="button"
              className="ods-button"
              onClick={exportXlsx}
              disabled={rows.length === 0}
            >
              Eksporter til Excel
            </button>
          </div>
        </div>

        <div className="ods-grid__column--12 ods-margin-top-3" style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {COLUMNS.map(c => (<th key={c.key} style={thStyle}>{c.label}</th>))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.Id}>
                  {COLUMNS.map(c => (<td key={c.key} style={tdStyle}>{cellValue(r, c.key)}</td>))}
                </tr>
              ))}
              {rows.length === 0 && !isLoading && (
                <tr>
                  <td style={tdStyle} colSpan={COLUMNS.length}>Ingen registreringer.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default withRouter(ExamRegistrationReportView);
